import { useContext, useEffect, useState, useRef, useCallback } from "react";

import "./GoogleMaps.css";

import _ from "lodash";
import Geocode from "react-geocode";
import { useMutation, useLazyQuery } from "@apollo/client";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
  DirectionsRenderer,
} from "@react-google-maps/api";
import { Popper } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useParams } from "react-router-dom";

import { GET_OFFICERS_ON_INCIDENT_FOR_MAP } from "../../Queries/GetOfficersOnIncidentForMap";
import { GET_ADDRESS } from "../../Queries/GetAddress";
import { UPDATE_GEOLOCATION } from "../../Mutations/UpdateGeolocation";

import { UserContext } from "../../Contexts/UserContext";
import { useToggle } from "../../CustomHooks/useToggle";
import { LAYERS_INFO, handleLayerToggle } from "./MapLayers";

import MapCloseButton from "./MapCloseButton";
import MapLayerController from "./MapLayerController";
import OfficerClusterer from "./OfficerClusterer";
import OfficerSearch from "./OfficerSearch";
import {
  useMapClickedInfoWindow,
  MapClickedInfoWindow,
} from "./MapClickedInfoWindow";

import { controlButtonDiv, controlButtonWrapper } from "./MapButtonStyling";
import { OfficerLocationProvider } from "./OfficerLocationContext";
import { traveledAtLeast } from "./mapUtils";

Geocode.setApiKey(import.meta.env.VITE_GOOGLE_MAPS_API_KEY);
Geocode.setLanguage("en");
Geocode.setRegion("us");
Geocode.setLocationType("ROOFTOP");
// Geocode.enableDebug();

const geoOptions = {
  enableHighAccuracy: false,
  maximumAge: 5000,
};

const useStyles = makeStyles(theme => ({
  paper: {
    backgroundColor: theme.palette.background.paper,
    border: "2px solid #000",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    maxHeight: "90vh",
    cursor: "move",
  },
}));

const SingleAddressMap = ({ avlEnabled, user, subscriptionServer }) => {
  const { addressId, incidentId } = useParams();
  const [addressPosition, setAddressPosition] = useState(null);
  const [userPosition, setUserPosition] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [updateGeolocation] = useMutation(UPDATE_GEOLOCATION);
  const { responseErrorHandler } = useContext(UserContext);
  const [zoom, setZoom] = useState(null);
  const [showIncidents, toggleShowIncidents] = useToggle(true);
  const [showOfficers, toggleShowOfficers] = useToggle(true);
  const [showOfficerAccuracy, toggleShowOfficerAccuracy] = useToggle(false);
  const [directionsResult, setDirectionsState] = useState(null);
  const [showDirections, toggleShowDirections] = useToggle(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showRoute, toggleShowRoute] = useToggle(false);
  const [openMapMenu, setOpenMapMenu] = useState(null);
  const showSearch = openMapMenu === "officer";
  const toggleShowSearch = value => {
    setOpenMapMenu(prev =>
      value === false || prev === "officer" ? null : "officer",
    );
  };

  const {
    handleMapClick,
    clickedLocation,
    clickedAddress,
    showClickedInfo,
    setShowClickedInfo,
    setClickedLocation,
    setClickedAddress,
  } = useMapClickedInfoWindow();

  const handleDirectionsClick = e => {
    toggleShowDirections();
    setAnchorEl(e.currentTarget);
  };
  const classes = useStyles();

  const [officers, _setOfficers] = useState(null);
  const [addressMarker, setAddressMarker] = useState(null);

  const officerStateRef = useRef(officers);
  const setOfficers = data => {
    officerStateRef.current = data;
    _setOfficers(data);
  };

  const _singleAddressMapIsMounted = useRef(true);

  useEffect(() => {
    return () => {
      _singleAddressMapIsMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (incidentId !== "null") {
      getOfficersOnIncidentForMap({
        variables: { originatorId: user?.id, incidentId: incidentId },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidentId, user?.id]);

  const [getOfficersOnIncidentForMap] = useLazyQuery(
    GET_OFFICERS_ON_INCIDENT_FOR_MAP,
    {
      context: { serializationKey: "incidentQueue" },
      fetchPolicy: "network-only",
      onError: error => {
        console.error(JSON.stringify(error));
      },
      onCompleted: data => {
        if (data) {
          responseErrorHandler(data);
          const res = data[Object.keys(data)[0]];
          let arr = [];
          res.officerIncidentSpecifics.forEach(ois => {
            if (!ois?.clearedAt && ois?.officer?.deptAttribute?.onDuty) {
              arr.push(ois?.officer);
            }
          });
          if (arr.length > 0) setOfficers(arr);
        } else {
          console.error(
            "There was an issue fetching the officers on the incident",
          );
        }
      },
    },
  );

  // useEffect(() => {
  //   if (import.meta.env.VITE_USE_POLLING.toLowerCase() === "true") {
  //     const interval = setInterval(() => {
  //       if (
  //         onlineState &&
  //         user?.isAuthenticated &&
  //         user?.id &&
  //         _singleAddressMapIsMounted &&
  //         incidentId !== "null"
  //       ) {
  //         getOfficersOnIncidentForMap({
  //           variables: {
  //             originatorId: user?.id,
  //             incidentId: incidentId,
  //             noSessionReset: true,
  //           },
  //         });
  //       }
  //     }, 10000);
  //     return () => {
  //       clearInterval(interval);
  //     };
  //   }
  // });

  const [getAddress] = useLazyQuery(GET_ADDRESS, {
    context: { serializationKey: "incidentQueue" },
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        const res = data[Object.keys(data)[0]];
        if (!res) {
          setAddressMarker(null);
          officerOrOriForCenter();
          return;
        }
        if (res.lat && res.lng) {
          setAddressPosition({
            lat: res.lat,
            lng: res.lng,
          });
          setAddressMarker({
            id: res?.id,
            name: res?.name,
            lat: res?.lat,
            lng: res?.lng,
            canMove: false,
          });
        } else if (res.name) {
          console.log("Geocoding for getAddress");
          Geocode.fromAddress(res.name).then(
            response => {
              const { lat, lng } = response.results[0].geometry.location;
              setAddressPosition({
                lat: lat,
                lng: lng,
              });
              setAddressMarker({
                id: res?.id,
                name: res?.name,
                lat: lat,
                lng: lng,
                canMove: false,
              });
            },
            error => {
              console.error(error);
              officerOrOriForCenter();
            },
          );
        } else {
          officerOrOriForCenter();
        }
      } else {
        console.error("There was an issue looking up the address");
      }
    },
  });

  const officerOrOriForCenter = () => {
    if (user?.ori?.oriAddress?.lat && user?.ori?.oriAddress?.lng) {
      setAddressPosition({
        lat: user?.ori?.oriAddress?.lat,
        lng: user?.ori?.oriAddress?.lng,
      });
    } else if (user?.ori?.oriAddress?.name) {
      console.log("Geocoding in officerOrOriForCenter");
      Geocode.fromAddress(user?.ori?.oriAddress?.name).then(
        response => {
          const { lat, lng } = response.results[0].geometry.location;
          setAddressPosition({ lat: lat, lng: lng });
        },
        error => {
          console.error(error);
          setAddressPosition({ lat: 44.4752228, lng: -73.2078055 });
          setZoom(9);
        },
      );
    } else {
      console.error("Home ORI address not found");
      setAddressPosition({ lat: 44.4752228, lng: -73.2078055 });
      setZoom(9);
    }
  };
  const userPositionRef = useRef(null);

  const geoSuccess = data => {
    const accuracy = Math.round(+data.coords?.accuracy) || null;
    const lat = parseFloat(data.coords?.latitude);
    const lng = parseFloat(data.coords?.longitude);

    const prev = userPositionRef.current;

    const shouldUpdate = traveledAtLeast(
      lat,
      lng,
      prev?.lat,
      prev?.lng,
      1, // 1 meter threshold
    );

    if (shouldUpdate || !prev?.lat || !prev?.lng) {
      updateGeolocation({
        variables: {
          input: {
            originatorId: user?.id,
            officerId: user?.id,
            lat,
            lng,
            accuracy,
            noSessionReset: true,
          },
        },
      });

      // Update both the ref and state
      userPositionRef.current = { lat, lng };
      setUserPosition({ lat, lng });

      if (officerStateRef.current) {
        const newOfficers = officerStateRef.current.map(officer => {
          if (officer?.id.toString() === user?.id.toString()) {
            const updated = _.cloneDeep(officer);
            updated.lat = lat;
            updated.lng = lng;
            updated.accuracy = accuracy;
            return updated;
          }
          return officer;
        });
        setOfficers(newOfficers);
      }
    } else {
      console.log(
        `Not updating location for user ${user?.id}; distance < 1 meter`,
      );
    }
  };

  const geoError = error => {
    console.error(`ERROR(${error.code}): ${error.message}`);
  };
  const mapRef = useRef(null);

  const onMapLoad = useCallback(map => {
    mapRef.current = map;
  }, []);

  // const panToLatLng = ({ lat, lng }) => {
  //   if (mapRef.current && typeof mapRef.current.panTo === "function") {
  //     mapRef.current.panTo({ lat, lng });
  //     mapRef.current.setZoom(17);
  //   }
  // };

  useEffect(() => {
    if (
      !window.google ||
      !window.google.maps ||
      !userPosition ||
      !addressPosition
    ) {
      return;
    }
    try {
      const DirectionsService = new window.google.maps.DirectionsService();

      DirectionsService.route(
        {
          origin: userPosition,
          destination: addressPosition,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK) {
            setDirectionsState(result);
          } else {
            console.error(`error fetching directions ${result}`);
          }
        },
      );
    } catch (err) {
      console.error(err);
    }
  }, [userPosition, addressPosition]);

  useEffect(() => {
    if (user?.id && user?.onDuty && avlEnabled) {
      const geo = navigator.geolocation;
      if (!geo) {
        alert("Geolocation tracking not enabled");
        return;
      } else {
        const watcher = geo.watchPosition(geoSuccess, geoError, geoOptions);
        console.log("watcher: ", watcher);
        return () => geo.clearWatch(watcher);
      }
    } else {
      console.log("Not tracking");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avlEnabled, user?.id, user?.onDuty]);

  useEffect(() => {
    getAddress({ variables: { addressId: addressId, originatorId: user?.id } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressId, user?.id]);

  const [mapFitted, setMapFitted] = useState(false);
  //for setting the map bounds
  useEffect(() => {
    if (!mapRef.current) return;
    const timeout = setTimeout(() => {
      const bounds = new window.google.maps.LatLngBounds();

      // Incident markers
      if (addressMarker.lat && addressMarker.lng) {
        bounds.extend(
          new window.google.maps.LatLng(
            Number(addressMarker.lat),
            Number(addressMarker.lng),
          ),
        );
      }

      // Officer markers
      if (Array.isArray(officers) && officers.length > 0) {
        officers.forEach(officer => {
          if (officer.lat && officer.lng) {
            bounds.extend(
              new window.google.maps.LatLng(
                Number(officer.lat),
                Number(officer.lng),
              ),
            );
          }
        });
      }

      if (!bounds.isEmpty() && !mapFitted) {
        mapRef.current.fitBounds(bounds);
        setMapFitted(true);
      }
    }, 0);

    return () => clearTimeout(timeout);
  }, [addressMarker, officers, mapFitted]);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY, // ,
  });
  const self = officers?.find(officer => officer?.id === user?.id);

  const [findOfficer, setFindOfficer] = useState(null);
  const findOfficerLocation = (officerId, latLngObj) => {
    setFindOfficer(officerId);

    if (
      latLngObj &&
      typeof latLngObj.lat !== "undefined" &&
      typeof latLngObj.lng !== "undefined" &&
      Number.isFinite(Number(latLngObj.lat)) &&
      Number.isFinite(Number(latLngObj.lng)) &&
      mapRef.current.panTo
    ) {
      mapRef.current.panTo({
        lat: Number(latLngObj.lat),
        lng: Number(latLngObj.lng),
      });
    } else {
      console.error("Invalid latLngObj provided to findOfficerLocation");
      return;
    }
  };

  if (isLoaded && addressPosition) {
    return (
      <div style={{ height: "100vh", width: "100vw" }}>
        <OfficerLocationProvider
          officers={officers}
          subscriptionServer={subscriptionServer}
        >
          <div style={controlButtonDiv}>
            <div style={controlButtonWrapper}>
              <MapLayerController
                options={Object.keys(LAYERS_INFO)
                  .filter(k => LAYERS_INFO[k]?.url)
                  .map(label => {
                    return { handler: handleLayerToggle, label: label };
                  })}
                map={mapRef.current}
                showIncidentToggle={true}
                toggleShowIncidents={toggleShowIncidents}
                showIncidents={showIncidents}
                showOfficerToggle={true}
                toggleShowOfficers={toggleShowOfficers}
                showOfficers={showOfficers}
                showDirectionsButton={true}
                showRouteButton={true}
                toggleShowDirections={handleDirectionsClick}
                directionsOpen={showDirections}
                toggleShowRoute={toggleShowRoute}
                routeOpen={showRoute}
                showOfficerAccuracy={showOfficerAccuracy}
                toggleShowOfficerAccuracy={toggleShowOfficerAccuracy}
                userId={user?.id}
                openMenu={openMapMenu}
                setOpenMenu={setOpenMapMenu}
              />
              <OfficerSearch
                showSearch={showSearch}
                toggleShowSearch={toggleShowSearch}
                findOfficerCallback={findOfficerLocation}
              />
            </div>
          </div>
          <MapCloseButton />
          <GoogleMap
            resetBoundsOnResize
            id="single-address-google-map"
            mapContainerStyle={{
              height: "100%",
              width: "100%",
            }}
            zoom={zoom || 15}
            onLoad={onMapLoad}
            center={
              addressPosition
                ? addressPosition
                : self
                  ? { lat: self?.lat, lng: self?.lng }
                  : { lat: 0, lng: 0 }
            }
            onClick={handleMapClick}
          >
            {showIncidents && addressMarker && (
              <Marker
                key={addressMarker?.id}
                title={addressMarker?.name}
                name={addressMarker?.name}
                position={{ lat: addressMarker?.lat, lng: addressMarker?.lng }}
                onClick={() => setIsOpen(addressMarker.id)}
              >
                {isOpen === addressMarker.id && (
                  <InfoWindow onCloseClick={() => setIsOpen(false)}>
                    <div>{addressMarker?.name}</div>
                  </InfoWindow>
                )}
              </Marker>
            )}

            {showRoute && (
              <DirectionsRenderer options={{ directions: directionsResult }} />
            )}
            <MapClickedInfoWindow
              clickedLocation={clickedLocation}
              clickedAddress={clickedAddress}
              showClickedInfo={showClickedInfo}
              setShowClickedInfo={setShowClickedInfo}
              setClickedLocation={setClickedLocation}
              setClickedAddress={setClickedAddress}
            />
            {showOfficers && (
              <OfficerClusterer
                map={mapRef.current}
                officers={officers}
                showOfficerAccuracy={showOfficerAccuracy}
                subscriptionServer={subscriptionServer}
                findOfficer={findOfficer}
                setFindOfficer={setFindOfficer}
              />
            )}
          </GoogleMap>
          <Popper
            keepMounted={true}
            className={classes.paper}
            anchorEl={anchorEl}
            open={showDirections}
            placement="bottom-start"
          >
            {directionsResult?.routes[0]?.legs[0]?.steps ? (
              <div>
                {directionsResult?.routes[0]?.legs[0]?.steps?.map(
                  (step, index) => {
                    const directionMarkup = { __html: step?.instructions };
                    return (
                      <span key={index}>
                        <span dangerouslySetInnerHTML={directionMarkup} />
                        <br />
                      </span>
                    );
                  },
                )}
              </div>
            ) : (
              <div>Loading...</div>
            )}
          </Popper>
        </OfficerLocationProvider>
      </div>
    );
  } else {
    return <div>Waiting</div>;
  }
};

export default SingleAddressMap;
